import { debounce } from '@mui/material/utils';
import { ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  DefaultEmptySearchResultsPlaceholder,
  DefaultErrorPlaceholder,
  DefaultNoDataPlaceholder,
} from '../table/data-table-placeholder';
import { GalleryViewMode, PaginatedItems } from './types';

export function GridView<T>({
  items,
  setSelected,
  page,
  setPage,
  pageSize,
  totalCount,
  viewMode,
  renderCard,
  isLoading,
  showError,
  searchValue,
  isFilterApplied,
  onClearSearch,
}: {
  items: PaginatedItems<T>;
  page: number;
  pageSize: number;
  totalCount: number;
  setSelected: (item: T) => void;
  setPage: (page: number) => void;
  viewMode: GalleryViewMode;
  isLoading: boolean;
  showError?: boolean;
  searchValue?: string;
  isFilterApplied?: boolean;
  onClearSearch?: () => void;
  renderCard: (
    item: T,
    viewMode: GalleryViewMode,
    setSelected: (item: T) => void,
  ) => ReactNode;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  const loadMore = useCallback(() => {
    if (!isLoading && totalCount && pageSize) {
      const newPage = page + 1;
      if (newPage * pageSize < totalCount) {
        setPage(newPage);
      }
    }
  }, [isLoading, totalCount, pageSize, page, setPage]);

  const debouncedLoadMore = useMemo(() => debounce(loadMore, 400), [loadMore]);

  useEffect(() => {
    return () => {
      debouncedLoadMore.clear();
    };
  }, [debouncedLoadMore]);

  const handleScroll = useCallback(() => {
    if (containerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
      if (scrollTop + clientHeight >= (scrollHeight * 4) / 5) {
        debouncedLoadMore();
      }
    }
  }, [debouncedLoadMore]);

  // adds a scroll event listener to the container to fetch data before the user reaches the bottom
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
    }
    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll]);

  const hasData = items && items.records && items.records.length > 0;

  // Determine placeholder to show
  let placeholder: ReactNode = null;
  if (!isLoading) {
    if (showError) {
      placeholder = <DefaultErrorPlaceholder />;
    } else if (!hasData && !searchValue && !isFilterApplied) {
      placeholder = <DefaultNoDataPlaceholder />;
    } else if ((searchValue || isFilterApplied) && !hasData) {
      placeholder = (
        <DefaultEmptySearchResultsPlaceholder
          clearSearch={() => {
            onClearSearch?.();
          }}
        />
      );
    }
  }

  return (
    <div className="grow overflow-y-auto" ref={containerRef} tabIndex={0}>
      {placeholder ? (
        <div className="flex items-center justify-center h-full">
          {placeholder}
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-4 pb-1">
          {items?.records?.map((item) =>
            renderCard(item, viewMode, setSelected),
          )}
        </div>
      )}
    </div>
  );
}
